import { toast } from 'sonner';
import React, { useState } from 'react';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../hooks/index.js';
import { generateUploadPromises, UploadFileModal, UploadFilesFormData, useFileUploader } from '../../../../file-upload';
import { getStepConfig } from '../../getStepConfig';
import { determineIfStepIsDisabled } from '../../utils';
import { usePatientIntakeStatusContext } from '../../hooks';
import { ActionPrompt, ViewDocumentModal } from '../../components';
import { toggleBaseStepVisibilityWithToast } from './utils';
import type { BaseStepsBaseProps } from './BaseSteps.types.ts';

type UploadModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose }) => {
  const { user, userType } = useAuth();
  const { uploader } = useFileUploader();

  const handleFileUploadOnSubmit = async ({ files, description }: UploadFilesFormData) => {
    if (!user || !userType || !user.email) {
      throw new Error('User not found');
    }

    const generateFileUploadData: Parameters<
      typeof generateUploadPromises<'PATIENT_DOCUMENT_UPLOAD'>
    >[0]['generateFileUploadData'] = ({ file }) => ({
      file,
      type: 'PATIENT_DOCUMENT_UPLOAD',
      ownerId: user.uid,
      uploaderId: user.uid,
      fileName: file.name,
      metadata: {
        patientId: user.uid,
        description: description.trim(),
        type: 'REFERRAL_LETTER_OF_SUPPORT',
        data: null,
      },
    });

    return await generateUploadPromises({ files, uploader, generateFileUploadData });
  };

  return (
    <UploadFileModal
      noToast
      isOpen={isOpen}
      handleCloseOnClick={onClose}
      onSubmit={handleFileUploadOnSubmit}
      maxFiles={1}
      description="Please upload your referral letter of support from your GP or specialist"
      defaultValues={{
        description: 'My referral letter of support',
      }}
    />
  );
};

type UploadDocumentActionModalProps = {
  step: IntakeStatusBaseSteps['UPLOAD_REFERRAL_LETTER_OF_SUPPORT'];
  isOpen: boolean;
  onClose: () => void;
};

const UploadDocumentActionModal: React.FC<UploadDocumentActionModalProps> = ({ step, isOpen, onClose }) => {
  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    return <ViewDocumentModal step={step} type="PATIENT_DOCUMENT_UPLOAD" isOpen={isOpen} onClose={onClose} />;
  }

  return <UploadModal isOpen={isOpen} onClose={onClose} />;
};

interface UploadReferralLetterOfSupportBaseStepActionProps extends BaseStepsBaseProps {}

export const UploadReferralLetterOfSupportBaseStepAction: React.FC<
  UploadReferralLetterOfSupportBaseStepActionProps
> = ({ onEdit }) => {
  const { user, userType } = useAuth();
  const { isHealthcareProvider, patientId } = usePatientIntakeStatusContext();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { baseSteps } = usePatientIntakeStatusContext();

  const step = baseSteps?.UPLOAD_REFERRAL_LETTER_OF_SUPPORT;

  const handleOnClick = () => {
    setIsUploadModalOpen(true);
  };

  const handleOnEdit = () => {
    onEdit();
  };

  const handleOnToggleDisabled = async () => {
    if (!step) {
      toast.error('Step not found');

      return;
    }

    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const isCurrentlyDisabled = determineIfStepIsDisabled({ step });

    await toggleBaseStepVisibilityWithToast({
      step,
      patientId,
      isCurrentlyDisabled,
      stepId: 'UPLOAD_REFERRAL_LETTER_OF_SUPPORT',
      context: 'upload referral letter of support',
      actionedBy: {
        id: user.uid,
        email: user.email,
        name: null,
        userType,
      },
    }).unwrap();
  };

  if (!step) {
    return null;
  }

  const stepConfig = getStepConfig('UPLOAD_REFERRAL_LETTER_OF_SUPPORT');

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  const description = stepConfig.statuses[step.currentStatus.status].description({
    step,
    isHealthcareProvider,
  });

  return (
    <>
      <ActionPrompt
        step={step}
        title={title}
        description={description}
        onClick={handleOnClick}
        onEdit={handleOnEdit}
        onToggleDisabled={handleOnToggleDisabled}
      />

      <UploadDocumentActionModal step={step} isOpen={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)} />
    </>
  );
};
